import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/http";
import { postFoodLog } from "@/lib/offlineMeals";
import { useT } from "@/i18n/I18nContext";
import { defaultMealTypeForNow } from "@/lib/mealTime";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { ChevronRight, Copy, Loader2, UtensilsCrossed, X, Star } from "lucide-react";
import { todayIso, shiftIso } from "@/lib/date";

const MEAL_ORDER = ["colazione", "spuntino", "pranzo", "merenda", "cena"];

/**
 * YourMealsSheet — "Deine Mahlzeiten": bottom sheet listing distinct meals of
 * the last 7 days; tap → edit-before-copy dialog → POST /client/food-log.
 * Shared by the Diary chip and the QuickTrackMenu (Dashboard + Diary).
 */
export default function YourMealsSheet({ open, onOpenChange, date = todayIso(), mealType = null, onSaved }) {
  const { t } = useT();
  const navigate = useNavigate();
  const [yLogs, setYLogs] = useState(null);
  const [favs, setFavs] = useState([]);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const mealLabel = (v) => ({ colazione: t("diary.breakfast"), spuntino: t("diary.snack"), pranzo: t("diary.lunch"), merenda: t("diary.afternoon"), cena: t("diary.dinner") }[v] || v);

  useEffect(() => { setYLogs(null); }, [date]);

  useEffect(() => {
    if (!open || yLogs !== null) return;
    Promise.all([
      api.get("/client/recent-meals?limit=50").catch(() => ({ data: [] })),
      api.get("/client/meal-favorites").catch(() => ({ data: [] })),
    ]).then(([{ data }, { data: favData }]) => {
      const cutoff = shiftIso(todayIso(), -7);
      setYLogs((data || []).filter((m) => (m.date || "") >= cutoff));
      setFavs(favData || []);
    });
  }, [open, yLogs]);

  const norm = (s) => (s || "").trim().toLowerCase();
  const favFor = (log) => favs.find((f) => norm(f.description) === norm(log.description));
  // Recent list without entries that are already favorites (they sit on top).
  const recent = (yLogs || []).filter((l) => !favFor(l));

  const toggleFav = async (e, log) => {
    e.stopPropagation();
    const existing = favFor(log);
    try {
      if (existing) {
        await api.delete(`/client/meal-favorites/${existing.favorite_id}`);
        setFavs((f) => f.filter((x) => x.favorite_id !== existing.favorite_id));
        toast.success(t("diary.favoriteRemoved"));
      } else {
        const { data } = await api.post("/client/meal-favorites", {
          description: log.description,
          meal_type: log.meal_type || null,
          components: log.components || [],
          calories: log.calories ?? null,
          protein: log.protein ?? null,
          carbs: log.carbs ?? null,
          fat: log.fat ?? null,
          photo: log.photo_base64 || log.photo || null,
        });
        setFavs((f) => [data, ...f]);
        toast.success(t("diary.favoriteAdded"));
      }
    } catch { toast.error(t("common.error")); }
  };

  const pick = (log) => {
    onOpenChange(false);
    const qs = new URLSearchParams({ date });
    if (log.log_id) qs.set("dup", log.log_id);
    else {
      // Favorites carry no log_id — hand the snapshot over via sessionStorage.
      try { sessionStorage.setItem("solano_dup_meal", JSON.stringify(log)); } catch { /* quota */ }
      qs.set("dup", "session");
    }
    const mt = mealType || log.meal_type;
    if (mt) qs.set("meal_type", mt);
    navigate(`/app/pasto?${qs.toString()}`);
  };

  const copyPayload = (log, mt) => ({
    date,
    meal_type: mt,
    description: log.description,
    calories: log.calories ?? null,
    protein: log.protein ?? null,
    carbs: log.carbs ?? null,
    fat: log.fat ?? null,
    fiber: log.fiber ?? null,
    sugar: log.sugar ?? null,
    saturated_fat: log.saturated_fat ?? null,
    salt: log.salt ?? null,
    notes: log.notes || null,
    photo_base64: null,
    components: Array.isArray(log.components) && log.components.length ? log.components : null,
  });

  const copyAllYesterday = async () => {
    const yesterday = shiftIso(todayIso(), -1);
    const list = (yLogs || []).filter((l) => l.date === yesterday);
    if (list.length === 0) return;
    setSaving(true);
    try {
      await Promise.all(list.map((log) => postFoodLog(copyPayload(log, log.meal_type))));
      toast.success(t("diary.allDuplicated").replace("{n}", list.length));
      onOpenChange(false);
      onSaved?.();
    } catch { toast.error(t("common.error")); }
    finally { setSaving(false); }
  };

  const yCount = (yLogs || []).filter((l) => l.date === shiftIso(todayIso(), -1)).length;

  // Long-press on a favorite → log it instantly (no dialog).
  const pressTimer = useRef(null);
  const longFired = useRef(false);
  const [pressingKey, setPressingKey] = useState(null);

  const quickLog = async (fav) => {
    if (saving) return;
    setSaving(true);
    try { navigator.vibrate?.(30); } catch {}
    try {
      await postFoodLog(copyPayload(fav, mealType || fav.meal_type || defaultMealTypeForNow()));
      toast.success(t("diary.favQuickLogged"));
      onOpenChange(false);
      onSaved?.();
    } catch { toast.error(t("common.error")); }
    finally { setSaving(false); }
  };

  const startPress = (fav, key) => {
    longFired.current = false;
    clearTimeout(pressTimer.current);
    setPressingKey(key);
    pressTimer.current = setTimeout(() => {
      longFired.current = true;
      setPressingKey(null);
      quickLog(fav);
    }, 550);
  };
  const cancelPress = () => { clearTimeout(pressTimer.current); setPressingKey(null); };
  useEffect(() => () => clearTimeout(pressTimer.current), []);

  const renderRow = (yl, i, isFav) => {
    const photo = yl.photo_base64 || yl.photo;
    const pressHandlers = isFav ? {
      onPointerDown: () => startPress(yl, i),
      onPointerUp: cancelPress,
      onPointerLeave: cancelPress,
      onPointerCancel: cancelPress,
      onContextMenu: (e) => e.preventDefault(),
    } : {};
    return (
      <div
        key={yl.log_id || yl.favorite_id || i}
        role="button"
        tabIndex={0}
        {...pressHandlers}
        onClick={() => { if (longFired.current) { longFired.current = false; return; } pick(yl); }}
        onKeyDown={(e) => e.key === "Enter" && pick(yl)}
        data-testid={`duplicate-item-${i}`}
        style={isFav ? { WebkitTouchCallout: "none", WebkitUserSelect: "none", userSelect: "none" } : undefined}
        className={`w-full flex items-center gap-3 rounded-2xl border ${isFav ? "border-[#E8951C]/30 bg-[#E8951C]/5" : "border-slate-100 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/40"} ${pressingKey === i ? "scale-[0.97] bg-[#E8951C]/15" : ""} hover:bg-slate-100 dark:hover:bg-slate-800/60 active:scale-[0.99] transition p-3 text-left cursor-pointer`}
      >
        {photo ? (
          <img
            src={photo}
            alt=""
            data-testid={`duplicate-item-photo-${i}`}
            onClick={(e) => { e.stopPropagation(); setPhotoPreview({ src: photo, title: yl.description || "" }); }}
            className="h-11 w-11 rounded-2xl object-cover shrink-0 ring-1 ring-slate-200/70 dark:ring-slate-700 cursor-zoom-in active:scale-95 transition"
          />
        ) : (
          <span className="h-11 w-11 rounded-2xl bg-[#5A8B8F]/12 text-[#2C5254] dark:text-slate-200 flex items-center justify-center shrink-0">
            <UtensilsCrossed className="h-5 w-5" />
          </span>
        )}
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{yl.description}</span>
          <span className="block text-xs text-slate-500 truncate">
            {mealLabel(yl.meal_type)}{yl.calories ? ` · ${yl.calories} kcal` : ""}{yl.date ? ` · ${yl.date}` : ""}
          </span>
        </span>
        <button
          type="button"
          onClick={(e) => toggleFav(e, yl)}
          onPointerDown={(e) => e.stopPropagation()}
          aria-label={isFav ? t("diary.favoriteRemove") : t("diary.favoriteAdd")}
          data-testid={`fav-toggle-${i}`}
          className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 active:scale-90 transition ${isFav ? "text-[#E8951C]" : "text-slate-300 hover:text-[#E8951C]"}`}
        >
          <Star className={`h-[18px] w-[18px] ${isFav ? "fill-current" : ""}`} />
        </button>
        <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
      </div>
    );
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          className="rounded-t-3xl max-h-[80vh] overflow-y-auto px-0 pt-2 bg-white dark:bg-[#1a1d1e] border-t border-slate-100 dark:border-slate-800"
          style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1.5rem)" }}
          data-testid="duplicate-menu"
        >
          <div className="mx-auto h-1.5 w-10 rounded-full bg-slate-200 dark:bg-slate-700 mb-3" />
          <SheetHeader className="px-6 text-left">
            <SheetTitle className="font-heading text-xl text-[#2C5254] dark:text-slate-100 flex items-center gap-2">
              <UtensilsCrossed className="h-5 w-5 text-[#2C5254] dark:text-slate-100" strokeWidth={2.2} />
              {t("diary.yourMeals")}
            </SheetTitle>
            <SheetDescription className="text-sm text-slate-500">
              {t("diary.yourMealsSubtitle")}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-3 px-4 space-y-2">
            {yLogs === null ? (
              <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
            ) : yLogs.length === 0 && favs.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-500" data-testid="no-meals-yesterday">
                {t("diary.noMealsYesterday")}
              </div>
            ) : (
              <>
                {favs.length > 0 && (
                  <p className="px-1 pt-1 text-[10px] uppercase tracking-widest font-bold text-[#E8951C] flex items-center gap-1" data-testid="favorites-heading">
                    <Star className="h-3 w-3 fill-current" /> {t("diary.favorites")}
                    <span className="ml-auto normal-case tracking-normal font-medium text-slate-400" data-testid="favorites-hint">{t("diary.favLongPressHint")}</span>
                  </p>
                )}
                {favs.map((f, i) => renderRow(f, `fav-${i}`, true))}
                {favs.length > 0 && recent.length > 0 && (
                  <p className="px-1 pt-2 text-[10px] uppercase tracking-widest font-bold text-slate-400" data-testid="recent-heading">
                    {t("diary.yourMeals")}
                  </p>
                )}
                {recent.map((yl, i) => renderRow(yl, i, false))}
                {yCount > 0 && (
                  <button
                    type="button"
                    onClick={copyAllYesterday}
                    disabled={saving}
                    data-testid="duplicate-all-btn"
                    className="w-full h-11 rounded-2xl border border-[#E8951C]/30 text-[#E8951C] text-sm font-semibold flex items-center justify-center gap-1.5 hover:bg-[#E8951C]/5 active:scale-[0.99] transition disabled:opacity-60"
                  >
                    <Copy className="h-3.5 w-3.5" /> {t("diary.duplicateAll")} ({yCount})
                  </button>
                )}
              </>
            )}
          </div>

          {photoPreview && (
            <div
              className="fixed inset-0 z-[120] bg-black/85 backdrop-blur-sm flex flex-col items-center justify-center p-6"
              style={{ pointerEvents: "auto" }}
              onClick={(e) => { e.stopPropagation(); setPhotoPreview(null); }}
              data-testid="photo-preview-overlay"
            >
              <button
                type="button"
                aria-label={t("common.close") || "Close"}
                data-testid="photo-preview-close"
                className="absolute top-5 right-5 h-10 w-10 rounded-full bg-white/15 text-white flex items-center justify-center active:scale-95 transition"
                onClick={(e) => { e.stopPropagation(); setPhotoPreview(null); }}
              >
                <X className="h-5 w-5" />
              </button>
              <img
                src={photoPreview.src}
                alt=""
                data-testid="photo-preview-img"
                className="max-h-[75vh] max-w-full rounded-3xl object-contain shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              />
              {photoPreview.title && (
                <p className="mt-4 text-white/90 text-sm font-medium text-center max-w-xs truncate" data-testid="photo-preview-title">
                  {photoPreview.title}
                </p>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

    </>
  );
}
